'use client';

import classNames from '@sindresorhus/class-names';
import Image from 'next/image';
import {useCallback, useEffect, useRef, useState} from 'react';

import chevron from '@shared/Articles/assets/chevron.svg';

import {ArticleFaqItem} from '@strapi/types/Articles';

import styles from './FaqItem.module.scss';

export default function FaqItem({item}: {item: ArticleFaqItem}) {
  const [open, setOpen] = useState(false);
  const toggleOpen = useCallback(() => setOpen((current) => !current), []);
  const ref = useRef<HTMLDivElement>(null);
  const [answerContainerStyle, setAnswerContainerStyle] =
    useState<React.CSSProperties>({});

  useEffect(() => {
    const element = ref.current;
    if (!element || !window.ResizeObserver) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      const computedStyle = window.getComputedStyle(element);
      setAnswerContainerStyle({
        '--content-height': `calc(${computedStyle.marginBottom} + ${computedStyle.height})`,
      } as React.CSSProperties);
    });
    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (!item.answer?.trim() && !item.answer_md?.__html?.trim()) {
    return null;
  }

  return (
    <div
      className={styles.root}
      itemProp="mainEntity"
      itemType="https://schema.org/Question"
      itemScope
    >
      <button
        className={classNames(styles.questionButton, open && styles.open)}
        onClick={toggleOpen}
        data-testid="faq-item-toggle"
      >
        <h3 itemProp="name" className={styles.questionText}>
          {item.question}
        </h3>
        <Image src={chevron} alt="chevron" className={styles.chevron} />
      </button>
      <div
        className={classNames(styles.answerContainer, open && styles.open)}
        itemScope
        itemProp="acceptedAnswer"
        itemType="https://schema.org/Answer"
        style={answerContainerStyle}
      >
        {item.answer_md?.__html ? (
          <div
            itemProp="text"
            className={styles.answerText}
            dangerouslySetInnerHTML={item.answer_md}
            ref={ref}
          />
        ) : (
          <div itemProp="text" className={styles.answerText} ref={ref}>
            {item.answer}
          </div>
        )}
      </div>
    </div>
  );
}
